import { React, useEffect, useState } from "react";
import CustomInput from "../components/CustomInput";
import { useLocation, useNavigate } from "react-router-dom";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import * as yup from "yup";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import Dropzone from "react-dropzone";
import { delImg, uploadImg, uploadModelAndAudioFiles } from "../features/upload/uploadSlice";
import {
  createNewTemple,
  getATemple,
  resetState,
  updateATemple,
} from "../features/temple/templeSlice";

let schema = yup.object().shape({
  name: yup.string().required("Temple name is Required"),
  location: yup.string().required("Location is Required"),
  deity: yup.string().required("Deity is Required"),
  description: yup.string().required("Description is Required"),
  era: yup.string().required("Historical era is Required"),
  architecturalStyle: yup.string().required("Architectural style is Required"),
  significance: yup.string().required("Religious significance is Required"),
  festivals: yup.string().required("Major festivals is Required"),
});

const AddTemple = ({ editId, editMode }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const getTempleId = editId || location.pathname.split("/")[3];
  const isInModal = location.state?.fromModal;
  const [modelFile, setModelFile] = useState(null);
  const [chantingAudio, setChantingAudio] = useState(null);

  const imgState = useSelector((state) => state?.upload?.images);
  const newTemple = useSelector((state) => state.temple);
  const {
    isSuccess,
    isError,
    isLoading,
    createdTemple,
    updatedTemple,
    templeName,
    templeLocation,
    templeDeity,
    templeDescription,
    templeColor,
    templeEra,
    templeArchitecturalStyle,
    templeSignificance,
    templeFestivals,
    templeModelFile,
    templeChantingAudio,
    templeIsActive,
  } = newTemple;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: getTempleId && templeName ? templeName : "",
      location: getTempleId && templeLocation ? templeLocation : "",
      deity: getTempleId && templeDeity ? templeDeity : "",
      description: getTempleId && templeDescription ? templeDescription : "",
      color: getTempleId && templeColor ? templeColor : "",
      era: getTempleId && templeEra ? templeEra : "",
      architecturalStyle: getTempleId && templeArchitecturalStyle ? templeArchitecturalStyle : "",
      significance: getTempleId && templeSignificance ? templeSignificance : "",
      festivals: getTempleId && templeFestivals ? templeFestivals : "",
      modelFile: getTempleId && templeModelFile ? templeModelFile : null,
      chantingAudio: getTempleId && templeChantingAudio ? templeChantingAudio : null,
      isActive: getTempleId && templeIsActive !== undefined ? templeIsActive : true,
    },
    validationSchema: schema,
    onSubmit: (values) => {
      console.log("Form values being submitted:", values);
      console.log("Model file:", values.modelFile);
      console.log("Audio file:", values.chantingAudio);
      
      // Prepare the data to send
      const dataToSend = {
        ...values,
        modelFile: values.modelFile && values.modelFile.public_id && values.modelFile.url ? {
          public_id: values.modelFile.public_id,
          url: values.modelFile.url
        } : null,
        chantingAudio: values.chantingAudio && values.chantingAudio.public_id && values.chantingAudio.url ? {
          public_id: values.chantingAudio.public_id,
          url: values.chantingAudio.url
        } : null
      };
      
      console.log("Processed modelFile for submission:", dataToSend.modelFile);
      console.log("Processed chantingAudio for submission:", dataToSend.chantingAudio);
      console.log("Data to send:", dataToSend);
      
      if (getTempleId !== undefined) {
        const data = { id: getTempleId, ...dataToSend };
        dispatch(updateATemple(data));
      } else {
        dispatch(createNewTemple(dataToSend));
        formik.resetForm();
        setModelFile(null);
        setChantingAudio(null);
        setTimeout(() => {
          dispatch(resetState());
        }, 3000);
      }
    },
  });

  // Reset form when switching between add and edit modes
  useEffect(() => {
    if (getTempleId !== undefined) {
      // Editing mode - fetch temple data
      dispatch(getATemple(getTempleId));
    } else {
      // Adding mode - reset form
      dispatch(resetState());
      setModelFile(null);
      setChantingAudio(null);
      if (formik) {
        formik.resetForm();
      }
    }
  }, [getTempleId, editMode, editId]);

  useEffect(() => {
    if (isSuccess && createdTemple) {
      toast.success("Temple Added Successfully!");
      if (!isInModal) {
        navigate("/admin/temples");
      }
    }
    if (isSuccess && updatedTemple) {
      toast.success("Temple Updated Successfully!");
      if (!isInModal) {
        navigate("/admin/temples");
      }
    }
    if (isError) {
      toast.error("Something Went Wrong!");
    }
  }, [isSuccess, isError, isLoading]);

  // Process model file from imgState (uploaded files)
  useEffect(() => {
    console.log("Processing imgState:", imgState);
    if (imgState && imgState.length > 0) {
      // Process each file individually
      let modelFileToSet = null;
      let audioFileToSet = null;
      
      imgState.forEach((img, index) => {
        console.log(`Processing file ${index}:`, img);
        if (img.url) {
          console.log(`File ${index} has URL:`, img.url);
          const lowerCaseUrl = img.url.toLowerCase();
          if (lowerCaseUrl.endsWith('.glb')) {
            // It's a model file - take the first one
            console.log(`File ${index} is a GLB model file`);
            if (!modelFileToSet) {
              modelFileToSet = img;
              console.log(`Setting modelFileToSet to:`, img);
            }
          } else if (lowerCaseUrl.endsWith('.mp3')) {
            // It's an audio file - take the first one
            console.log(`File ${index} is an MP3 audio file`);
            if (!audioFileToSet) {
              audioFileToSet = img;
              console.log(`Setting audioFileToSet to:`, img);
            }
          } else {
            console.log(`File ${index} is neither GLB nor MP3`);
          }
        } else {
          console.log(`File ${index} has no URL`);
        }
      });
      
      // Set model file if found
      if (modelFileToSet) {
        console.log("Setting modelFile to:", modelFileToSet);
        setModelFile(modelFileToSet);
      } else {
        console.log("No model file found");
      }
      
      // Set audio file if found
      if (audioFileToSet) {
        console.log("Setting chantingAudio to:", audioFileToSet);
        setChantingAudio(audioFileToSet);
      } else {
        console.log("No audio file found");
      }
    } else {
      console.log("No imgState or empty imgState");
    }
  }, [imgState]);

  // Process existing temple files (for edit mode)
  useEffect(() => {
    if (getTempleId && templeModelFile) {
      // For edit mode, set as single object to match formik structure
      setModelFile(templeModelFile);
    } else if (!getTempleId) {
      // Clear files when switching to add mode
      setModelFile(null);
    }
    
    if (getTempleId && templeChantingAudio) {
      // For edit mode, set as single object to match formik structure
      setChantingAudio(templeChantingAudio);
    } else if (!getTempleId) {
      // Clear files when switching to add mode
      setChantingAudio(null);
    }
  }, [templeModelFile, templeChantingAudio, getTempleId, editMode, editId]);

  // Update formik values when files change
  useEffect(() => {
    console.log("modelFile changed to:", modelFile);
    if (formik) {
      formik.setFieldValue("modelFile", modelFile);
    }
  }, [modelFile]);

  useEffect(() => {
    console.log("chantingAudio changed to:", chantingAudio);
    if (formik) {
      formik.setFieldValue("chantingAudio", chantingAudio);
    }
  }, [chantingAudio]);

  // Update form values when temple data is loaded for editing
  useEffect(() => {
    if (getTempleId && formik) {
      formik.setValues({
        name: templeName || "",
        location: templeLocation || "",
        deity: templeDeity || "",
        description: templeDescription || "",
        color: templeColor || "",
        era: templeEra || "",
        architecturalStyle: templeArchitecturalStyle || "",
        significance: templeSignificance || "",
        festivals: templeFestivals || "",
        modelFile: templeModelFile || null,
        chantingAudio: templeChantingAudio || null,
        isActive: templeIsActive !== undefined ? templeIsActive : true,
      });
    } else if (!getTempleId && formik) {
      // Reset form for add mode
      formik.setValues({
        name: "",
        location: "",
        deity: "",
        description: "",
        color: "",
        era: "",
        architecturalStyle: "",
        significance: "",
        festivals: "",
        modelFile: null,
        chantingAudio: null,
        isActive: true,
      });
      setModelFile(null);
      setChantingAudio(null);
    }
  }, [
    templeName, templeLocation, templeDeity, templeDescription, templeColor,
    templeEra, templeArchitecturalStyle, templeSignificance, templeFestivals,
    templeModelFile, templeChantingAudio, templeIsActive,
    getTempleId, editMode, editId
  ]);

  const removeModelFile = (public_id) => {
    setModelFile(null);
  };

  const removeChantingAudio = (public_id) => {
    setChantingAudio(null);
  };

  const removeUploadedModelFile = (public_id) => {
    setModelFile(null);
    dispatch(delImg(public_id));
  };

  const removeUploadedChantingAudio = (public_id) => {
    setChantingAudio(null);
    dispatch(delImg(public_id));
  };

  return (
    <div>
      <h3 className="mb-4 title">
        {editMode || (getTempleId !== undefined) ? "Edit" : "Add"} Temple
      </h3>
      <div>
        <form
          onSubmit={formik.handleSubmit}
          className="d-flex gap-3 flex-column"
        >
          <CustomInput
            type="text"
            label="Enter Temple Name"
            name="name"
            onChng={formik.handleChange("name")}
            onBlr={formik.handleBlur("name")}
            val={formik.values.name}
          />
          <div className="error">
            {formik.touched.name && formik.errors.name}
          </div>
          
          <CustomInput
            type="text"
            label="Enter Location"
            name="location"
            onChng={formik.handleChange("location")}
            onBlr={formik.handleBlur("location")}
            val={formik.values.location}
          />
          <div className="error">
            {formik.touched.location && formik.errors.location}
          </div>
          
          <CustomInput
            type="text"
            label="Enter Deity"
            name="deity"
            onChng={formik.handleChange("deity")}
            onBlr={formik.handleBlur("deity")}
            val={formik.values.deity}
          />
          <div className="error">
            {formik.touched.deity && formik.errors.deity}
          </div>
          
          <div className="">
            <label className="form-label">Description</label>
            <textarea
              className="form-control"
              name="description"
              onChange={formik.handleChange("description")}
              onBlur={formik.handleBlur("description")}
              value={formik.values.description}
              rows="4"
              placeholder="Enter detailed description of the temple..."
            />
          </div>
          <div className="error">
            {formik.touched.description && formik.errors.description}
          </div>
          
          <CustomInput
            type="text"
            label="Enter Color (Optional)"
            name="color"
            onChng={formik.handleChange("color")}
            onBlr={formik.handleBlur("color")}
            val={formik.values.color}
          />
          
          <CustomInput
            type="text"
            label="Enter Historical Era"
            name="era"
            onChng={formik.handleChange("era")}
            onBlr={formik.handleBlur("era")}
            val={formik.values.era}
          />
          <div className="error">
            {formik.touched.era && formik.errors.era}
          </div>
          
          <CustomInput
            type="text"
            label="Enter Architectural Style"
            name="architecturalStyle"
            onChng={formik.handleChange("architecturalStyle")}
            onBlr={formik.handleBlur("architecturalStyle")}
            val={formik.values.architecturalStyle}
          />
          <div className="error">
            {formik.touched.architecturalStyle && formik.errors.architecturalStyle}
          </div>
          
          <div className="">
            <label className="form-label">Religious Significance</label>
            <textarea
              className="form-control"
              name="significance"
              onChange={formik.handleChange("significance")}
              onBlur={formik.handleBlur("significance")}
              value={formik.values.significance}
              rows="3"
              placeholder="Enter religious significance of the temple..."
            />
          </div>
          <div className="error">
            {formik.touched.significance && formik.errors.significance}
          </div>
          
          <CustomInput
            type="text"
            label="Enter Major Festivals"
            name="festivals"
            onChng={formik.handleChange("festivals")}
            onBlr={formik.handleBlur("festivals")}
            val={formik.values.festivals}
          />
          <div className="error">
            {formik.touched.festivals && formik.errors.festivals}
          </div>
          
          {/* 3D Model File Upload */}
          <div className="mt-4">
            <h5 className="mb-3">3D Model File (GLB format)</h5>
            <div className="bg-white border-1 p-5 text-center">
              <Dropzone
                onDrop={(acceptedFiles) => {
                  // Filter for GLB files only
                  const glbFiles = acceptedFiles.filter(file => 
                    file.name.endsWith('.glb') || file.name.endsWith('.GLB')
                  );
                  if (glbFiles.length > 0) {
                    dispatch(uploadModelAndAudioFiles(glbFiles));
                  } else {
                    toast.error("Please upload only GLB files for 3D models");
                  }
                }}
              >
                {({ getRootProps, getInputProps }) => (
                  <section>
                    <div {...getRootProps()}>
                      <input {...getInputProps()} />
                      <p>
                        Drag 'n' drop GLB file here, or click to select file
                      </p>
                      <p className="text-muted small">
                        Only GLB files are accepted for 3D models
                      </p>
                    </div>
                  </section>
                )}
              </Dropzone>
            </div>
            <div className="showimages d-flex flex-wrap gap-3 mt-3">
              {modelFile && modelFile.url && (
                <div className="position-relative">
                  <button
                    type="button"
                    onClick={() => removeUploadedModelFile(modelFile.public_id)}
                    className="btn-close position-absolute"
                    style={{ top: "10px", right: "10px" }}
                  ></button>
                  <div className="border rounded p-2 text-center">
                    <div className="text-muted small">3D Model</div>
                    <div className="fw-bold">{modelFile.url.split('/').pop()}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Chanting Audio File Upload */}
          <div className="mt-4">
            <h5 className="mb-3">Chanting Audio File (MP3 format)</h5>
            <div className="bg-white border-1 p-5 text-center">
              <Dropzone
                onDrop={(acceptedFiles) => {
                  // Filter for MP3 files only
                  const mp3Files = acceptedFiles.filter(file => 
                    file.name.endsWith('.mp3') || file.name.endsWith('.MP3')
                  );
                  if (mp3Files.length > 0) {
                    dispatch(uploadModelAndAudioFiles(mp3Files));
                  } else {
                    toast.error("Please upload only MP3 files for chanting audio");
                  }
                }}
              >
                {({ getRootProps, getInputProps }) => (
                  <section>
                    <div {...getRootProps()}>
                      <input {...getInputProps()} />
                      <p>
                        Drag 'n' drop MP3 file here, or click to select file
                      </p>
                      <p className="text-muted small">
                        Only MP3 files are accepted for chanting audio
                      </p>
                    </div>
                  </section>
                )}
              </Dropzone>
            </div>
            <div className="showimages d-flex flex-wrap gap-3 mt-3">
              {chantingAudio && chantingAudio.url && (
                <div className="position-relative">
                  <button
                    type="button"
                    onClick={() => removeUploadedChantingAudio(chantingAudio.public_id)}
                    className="btn-close position-absolute"
                    style={{ top: "10px", right: "10px" }}
                  ></button>
                  <div className="border rounded p-2 text-center">
                    <div className="text-muted small">Audio File</div>
                    <div className="fw-bold">{chantingAudio.url.split('/').pop()}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Active Status Toggle */}
          <div className="form-check mt-4">
            <input
              type="checkbox"
              className="form-check-input"
              id="isActive"
              name="isActive"
              onChange={formik.handleChange("isActive")}
              onBlur={formik.handleBlur("isActive")}
              checked={formik.values.isActive}
            />
            <label className="form-check-label" htmlFor="isActive">
              Temple is Active
            </label>
          </div>

          <button
            className="btn btn-success border-0 rounded-3 my-5"
            type="submit"
          >
            {editMode || (getTempleId !== undefined) ? "Edit" : "Add"} Temple
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddTemple;